/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Button, Center, Checkbox, Loader, Select, Space, Text } from '@mantine/core';
import { FC, useContext, useState } from 'react';
import { i18n } from '@lingui/core';
import { IconChevronDown, IconTemplate } from '@tabler/icons';
import { useParams } from 'react-router-dom';
import { RecommendationS } from '../../../../Service/RecommendationS';
import { BookingState, RecommendationDto } from '../../../../Types/RecommendationT';
import TemplateMessageToolbar from '../../../../Organismns/tiptap/toolbars/TemplateMessageToolbar';
import { openCreateTemplateModal } from '../../../../Templates/Settings/Emails/agency/AgencyTemplateModals';
import { EventBaseInput, EventRichInput } from '../../../../Organismns/tiptap/TiptapEditorInput';
import BaseToolbar from '../../../../Organismns/tiptap/toolbars/BaseToolbar';
import { DataS } from '../../../../Service/DataS';
import { EventS } from '../../../../Service/EventS';
import { useRecommendationMail } from '../../../../Service/hooks/useRecommendationMail';
import { StringUtils } from '../../../../Utils/utils';
import { useAgencyTemplates } from '../../../../Service/hooks/useAgencyTemplates';
import { AgencyTemplateDTO } from '../../../../Types/MessageT';
import { Contact } from '../../../../Service/ContactS';
import { EventContext } from '../../../../Service/Context/EventContext';
import { ProfileContext } from '../../../../Service/Context/ProfileContext';
import Attachments from '../../../../Organismns/Attachments';
import { PlaceholderS } from '../../../../Service/PlaceholderS';
import { TiptapS } from '../../../../Service/TiptapS';
import { openAttachmentSelectionModal } from '../../../../Atoms/AttachmentSelectionModal';

interface Props {
  newBookingState: BookingState;
  patchRecommendation: (recommendation: RecommendationDto) => void;
  contactEmail: string | undefined | null;
  recommendedContact: Contact;
  recommendation: RecommendationDto;
  closeModal: () => void;
}

const RecommendationBookingStateModal: FC<Props> = ({
  newBookingState,
  patchRecommendation,
  contactEmail,
  recommendedContact,
  recommendation,
  closeModal,
}) => {
  const { id: recommendationId } = recommendation;
  const { profile } = useContext(ProfileContext);
  const { id: eventId } = useParams();
  const { id: profileId, alias } = profile ?? {};
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, selectTemplate] = useState<AgencyTemplateDTO>();
  const { subject, message, attachments, handlers, updateProp, lastUpdate } = useRecommendationMail(
    'CHANGE_STATUS',
    recommendation,
    selectedTemplate,
    newBookingState,
  );
  const [sendEmail, setSendEmail] = useState(false);
  const { templates } = useAgencyTemplates();
  const { dates } = useContext<any>(EventContext);
  const recommendationDates = RecommendationS.extractRecommendationDates(dates, recommendation);

  const { setState: setAttachments, remove: removeAttachment } = handlers;

  const onSubmit = async () => {
    setLoading(true);

    const placeholdersResolved = await PlaceholderS.checkPlaceholders({
      profileId,
      eventId: Number(eventId),
      message,
      subject,
    });
    if (placeholdersResolved) {
      const patchFn = () =>
        sendEmail
          ? RecommendationS.editAndInform(recommendationId, {
              bookingState: newBookingState,
              subject,
              message,
              newVersion: true,
            })
          : RecommendationS.edit(recommendationId, { bookingState: newBookingState });

      const resJSON = await patchFn();
      if (resJSON) {
        patchRecommendation(resJSON);
        closeModal();
      }
    }
    setLoading(false);
  };

  return (
    <>
      <Text>
        Du kannst entweder nur den Status ändern oder auch zusätzlich den Dienstleister über die Statusänderung per
        E-Mail informieren.
      </Text>
      <Checkbox
        size="xs"
        mt="md"
        label="Ja, Dienstleister per E-Mail benachrichtigen"
        disabled={loading || !contactEmail}
        checked={sendEmail}
        onChange={() => setSendEmail(!sendEmail)}
      />
      {sendEmail && (
        <>
          <Select
            mb="xs"
            label={
              <Text size="xs" color="dimmed">
                Vorlage
              </Text>
            }
            icon={<IconTemplate size={20} />}
            data={DataS.getAgencyTemplateOptions(templates)}
            value={selectedTemplate ? selectedTemplate.id : undefined}
            onChange={(val) => selectTemplate(templates.find((t) => t.id === val))}
            styles={{ rightSection: { pointerEvents: 'none' } }}
            rightSection={<IconChevronDown size={14} />}
            rightSectionWidth={25}
            nothingFound="Keine Vorlagen"
          />
          <EventBaseInput
            updateTrigger={lastUpdate}
            toolbar={<BaseToolbar />}
            label="Betreff"
            value={subject}
            onUpdate={(html) => updateProp({ subject: html })}
          />
          <Space h="sm" />
          <Attachments
            attachments={attachments}
            documentBlueprints={[]}
            deleteFileAttachment={removeAttachment}
            deleteDocumentAttachment={undefined}
            eventId={undefined}
          />
          <Space h={attachments.length > 0 ? 'xl' : 'sm'} />

          <Text mb="sm">{`Hey ${RecommendationS.getGreeting(recommendedContact)},`} </Text>
          <Text mb="sm">
            {`${alias} hat den Status der Vermittlung für ein Event am ${StringUtils.concatStrings(
              recommendationDates.map(EventS.formatEventDate),
            )} auf ${i18n._(`recommendation.bookingState.${newBookingState.toLowerCase()}`)} gesetzt.`}
          </Text>
          <EventRichInput
            updateTrigger={lastUpdate}
            isEmailInput
            styles={TiptapS.TextAreaStyles}
            label="Nachricht an den Dienstleister"
            value={message}
            onUpdate={(html) => updateProp({ message: html })}
            toolbar={
              <TemplateMessageToolbar
                addAttachment={() =>
                  eventId &&
                  profileId &&
                  openAttachmentSelectionModal({
                    eventId,
                    profileId,
                    initialAttachmnets: attachments,
                    onSave: (_attachments) => {
                      setAttachments(_attachments);
                      return Promise.resolve(true);
                    },
                  })
                }
                saveAsTemplate={() => openCreateTemplateModal({ templateDto: { subject, message, attachments } })}
              />
            }
          />
          <Text mt="xs" size="xs" color="dimmed">
            Hinweis: Diese Nachricht wird zur Zeit noch nicht im System gespeichert - Du kannst Sie also im nicht noch
            einmal einsehen.
          </Text>
        </>
      )}
      <Center>
        <Box sx={{ width: '60%' }}>
          <Button
            fullWidth
            mt="xl"
            mb="xs"
            disabled={loading}
            onClick={onSubmit}
            leftIcon={loading && <Loader size="xs" />}
          >
            {i18n._('recommendation.bookingState.changed')}
          </Button>
          <Button disabled={loading} fullWidth color="gray" variant="subtle" onClick={closeModal}>
            {i18n._('actions.cancel')}
          </Button>
        </Box>
      </Center>
    </>
  );
};

export default RecommendationBookingStateModal;
